import type { HelpGuide } from '../types';

export const journalGuide: HelpGuide = {
  key: 'journal',
  title: 'บันทึกบัญชี (Journal Entries)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ บันทึกบัญชี',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/journal/คู่มือการใช้งาน_บันทึกบัญชี_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “บันทึกบัญชี” คือที่สำหรับบันทึกใบสำคัญทั่วไป (Journal Voucher — JV) ของบริษัท เช่น การบันทึกรายการขาย/ซื้อ การตั้งค่าใช้จ่ายค้างจ่าย การปรับปรุงบัญชี และรายการปรับปรุงสิ้นงวด ทุกใบสำคัญบันทึกตามหลักบัญชีคู่ — ยอดรวมด้านเดบิตต้องเท่ากับด้านเครดิตเสมอ',
        },
        {
          kind: 'para',
          text: 'ทุกใบสำคัญเดินตามการควบคุมแบบ “สี่ตา” (Maker–Checker): ผู้จัดทำ → ผู้ตรวจสอบ → ผู้อนุมัติ → ผ่านรายการ (โพสต์เข้าบัญชีแยกประเภท)',
        },
        {
          kind: 'callout',
          text: 'ระบบบันทึกว่า “ใครทำอะไร เมื่อใด” ไว้ทั้งหมด และคนที่จัดทำใบสำคัญจะอนุมัติใบของตัวเองไม่ได้ (หลักการแบ่งแยกหน้าที่ — Segregation of Duties)',
        },
        {
          kind: 'image',
          src: '/help/journal/01-list.png',
          caption: 'ภาพรวมหน้าบันทึกบัญชี — แถบสถานะ ตารางใบสำคัญ และป้ายบอกสถานะของแต่ละใบ',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'ปุ่มคำสั่งมุมขวาบน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'นำเข้า — ', text: 'สร้างใบสำคัญทีละหลายรายการจากไฟล์ Excel' },
            { lead: 'ส่งออก — ', text: 'ดาวน์โหลดรายการใบสำคัญที่เห็นบนหน้าจอเป็นไฟล์ Excel' },
            {
              lead: 'สร้างใบสำคัญ — ',
              text: 'เปิดฟอร์มสร้างใบสำคัญใหม่ (ปุ่มสีแดง — ใช้บ่อยที่สุด)',
            },
          ],
        },
        {
          kind: 'para',
          tone: 'muted',
          text: 'หมายเหตุ: ปุ่มที่แต่ละคนเห็นขึ้นอยู่กับสิทธิ์การใช้งาน ผู้ที่ไม่มีสิทธิ์สร้างจะไม่เห็นปุ่ม สร้างใบสำคัญ',
        },
        { kind: 'para', text: 'แท็บสถานะ 6 แท็บ (ตัวเลขในวงเล็บ = จำนวนใบในแท็บนั้น):' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ทั้งหมด — ', text: 'ดูทุกใบสำคัญ' },
            { lead: 'ฉบับร่าง / ส่งกลับแก้ — ', text: 'ใบที่ยังจัดทำไม่เสร็จ หรือถูกตีกลับมาแก้' },
            { lead: 'รออนุมัติ — ', text: 'ใบที่ส่งแล้วกำลังรอผู้ตรวจสอบ/ผู้อนุมัติพิจารณา' },
            {
              lead: 'อนุมัติแล้ว · รอผ่านรายการ — ',
              text: 'ใบที่อนุมัติครบแล้ว เหลือเพียงการผ่านรายการ',
            },
            { lead: 'โพสต์แล้ว — ', text: 'ใบที่ผ่านรายการเข้าบัญชีแยกประเภทเรียบร้อย' },
            { lead: 'ไม่อนุมัติ — ', text: 'ใบที่ถูกปฏิเสธ' },
          ],
        },
        {
          kind: 'para',
          text: 'ตัวกรอง: ช่องค้นหา (เลขที่ใบสำคัญ/รายละเอียด/ผู้บันทึก) และช่วงวันที่ (จาก…ถึง…) พร้อมปุ่ม รีเซ็ต ตารางมีคอลัมน์: เลขที่ใบสำคัญ · วันที่ · รายการ · งวด · เดบิต · เครดิต · สถานะ · ผู้บันทึก — คลิกที่แถวเพื่อเปิดดูรายละเอียดทั้งใบ',
        },
      ],
    },
    {
      id: 'create',
      heading: '3. การสร้างใบสำคัญใหม่',
      blocks: [
        { kind: 'para', text: 'กดปุ่ม สร้างใบสำคัญ (สีแดง) ระบบจะเปิดฟอร์มขึ้นมา' },
        { kind: 'para', text: 'ขั้นที่ 1 — กรอกหัวเอกสาร:' },
        {
          kind: 'steps',
          items: [
            {
              title: 'วันที่บันทึกบัญชี',
              text: 'ต้องอยู่ใน “งวดที่เปิด” เท่านั้น (ป้ายงวดสีเขียว “เปิด”) ถ้าเลือกวันในงวดที่ปิด/ยังไม่เปิด ระบบเตือนสีแดง',
            },
            { title: 'ประเภทเอกสาร', text: 'ค่าเริ่มต้นคือ JV เลือกเปลี่ยนได้จากรายการแบบค้นหา' },
            {
              title: 'สาขา / หน่วยงาน (ค่าเริ่มต้นทั้งใบ)',
              text: 'เลือกได้ตามต้องการ ใส่ลงทุกบรรทัดที่ไม่ได้ระบุเอง',
            },
            { title: 'รายละเอียด', text: 'คำอธิบายของใบสำคัญ' },
          ],
        },
        {
          kind: 'image',
          src: '/help/journal/02-create-empty.png',
          caption: 'ฟอร์มเริ่มต้น — งวด “มิถุนายน 2026 · เปิด” และแถบสมดุลขึ้น “✕ ยอดยังไม่สมดุล”',
        },
        { kind: 'para', text: 'ขั้นที่ 2 — กรอกบรรทัดเดบิต/เครดิต และตรวจแถบสมดุล:' },
        {
          kind: 'bullets',
          items: [
            { text: 'เลือกบัญชี (พิมพ์ค้นหาได้ทั้งรหัสและชื่อ) ใส่จำนวนในช่อง เดบิต หรือ เครดิต' },
            { lead: 'วางจาก Excel — ', text: 'วางข้อมูลหลายบรรทัดจาก Excel ได้ในครั้งเดียว' },
            { lead: 'เติมยอดให้สมดุล — ', text: 'ให้ระบบเติมยอดส่วนต่างให้อัตโนมัติ' },
            {
              lead: 'เพิ่มบรรทัด — ',
              text: 'เพิ่มบรรทัดใหม่ (หรือกด Enter ที่ช่องจำนวนบรรทัดสุดท้าย)',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'แถบสมดุลด้านล่างต้องขึ้น “✓ สมดุล” (สีเขียว) คือเดบิต = เครดิต ก่อนจึงจะกดบันทึกได้',
        },
        {
          kind: 'image',
          src: '/help/journal/03-create-filled.png',
          caption:
            'ตัวอย่างการขายเป็นเงินเชื่อที่กรอกครบและสมดุล — เดบิต 1,070,000 = เครดิต 1,070,000',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'บันทึกร่าง — ', text: 'บันทึกเป็นฉบับร่าง (ยังไม่ส่งอนุมัติ) แล้วปิดฟอร์ม' },
            { lead: 'บันทึก & สร้างใหม่ — ', text: 'บันทึกแล้วล้างฟอร์มเพื่อสร้างใบถัดไปทันที' },
          ],
        },
        {
          kind: 'para',
          tone: 'muted',
          text: 'เลขที่ใบสำคัญออกให้อัตโนมัติเมื่อบันทึก (เช่น JV-2026-06-0001) ไม่ต้องกรอกเอง',
        },
      ],
    },
    {
      id: 'lifecycle',
      heading: '4. วงจรเอกสารและการอนุมัติ (สี่ตา)',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกที่แถวในตารางเพื่อเปิด “การ์ดรายละเอียด” ด้านล่าง แสดงทั้งใบพร้อมปุ่มที่เปลี่ยนตามสถานะและสิทธิ์ ด้านบนมีแถบวงจร: ร่าง → รออนุมัติ → อนุมัติแล้ว → โพสต์แล้ว',
        },
        {
          kind: 'image',
          src: '/help/journal/04-detail-posted.png',
          caption:
            'การ์ดของใบที่ “โพสต์แล้ว” — บรรทัดบัญชี ประวัติการอนุมัติ (ผู้จัดทำ→ผู้ตรวจสอบ→ผู้อนุมัติ) และประวัติการดำเนินการ',
        },
        { kind: 'para', text: 'ผู้จัดทำ (ใบ ฉบับร่าง / ส่งกลับแก้ไข): แก้ไข · ส่งอนุมัติ · ลบ' },
        { kind: 'para', text: 'ผู้ตรวจสอบ / ผู้อนุมัติ (ใบ รออนุมัติ): กดปุ่ม พิจารณา แล้วเลือก' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'อนุมัติ — ',
              text: 'ผ่านการพิจารณา (ส่งต่อขั้นถัดไป หรือจบเป็น “อนุมัติแล้ว”)',
            },
            { lead: 'ตีกลับแก้ไข — ', text: 'ส่งกลับให้ผู้จัดทำแก้ พร้อมเหตุผล' },
            { lead: 'ไม่อนุมัติ — ', text: 'ปฏิเสธพร้อมเหตุผล' },
          ],
        },
        {
          kind: 'image',
          src: '/help/journal/05a-detail-pending.png',
          caption: 'ใบที่ “รออนุมัติ” — ขั้นที่กำลังรอถูกเน้นสีส้ม พร้อมปุ่ม พิจารณา',
        },
        {
          kind: 'image',
          src: '/help/journal/05b-decision.png',
          caption: 'หน้าต่างพิจารณา — เลือก อนุมัติ / ตีกลับแก้ไข / ไม่อนุมัติ และใส่เหตุผล',
        },
        {
          kind: 'callout',
          text: 'ผู้ที่ “ส่งอนุมัติ” ใบนั้นเองจะพิจารณาใบของตัวเองไม่ได้ (แบ่งแยกหน้าที่) และต้องถือบทบาทที่ตรงกับขั้นนั้น',
        },
        {
          kind: 'para',
          text: 'เมื่ออนุมัติครบทุกขั้น ใบอยู่สถานะ “อนุมัติแล้ว · รอผ่านรายการ” ผู้มีสิทธิ์กด ผ่านรายการ เพื่อลงบัญชีเข้าสมุดแยกประเภทจริง (เปลี่ยนเป็น “โพสต์แล้ว”) ใบที่โพสต์แล้วมีปุ่ม พิมพ์ และ ดู GL',
        },
      ],
    },
    {
      id: 'import-export',
      heading: '5. การนำเข้าและส่งออกข้อมูล',
      blocks: [
        {
          kind: 'para',
          text: 'ส่งออก: ดาวน์โหลดทะเบียนใบสำคัญที่แสดงบนหน้าจอ (ตามตัวกรองปัจจุบัน) เป็นไฟล์ Excel',
        },
        {
          kind: 'para',
          text: 'นำเข้าใบสำคัญอย่างง่าย: หนึ่งแถว = หนึ่งใบสำคัญ ที่มีบัญชีเดบิตหนึ่งบัญชีและเครดิตหนึ่งบัญชีในจำนวนเท่ากัน (ใบหลายบรรทัดให้ใช้ปุ่ม สร้างใบสำคัญ) หน้าต่างมีปุ่มดาวน์โหลดไฟล์ตัวอย่าง (Template)',
        },
        {
          kind: 'image',
          src: '/help/journal/06-import.png',
          caption:
            'หน้าต่างนำเข้าใบสำคัญอย่างง่าย — คอลัมน์ที่ต้องกรอก ตัวอย่างข้อมูล และปุ่มดาวน์โหลด Template',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '6. สรุปสถานะของใบสำคัญ',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย', 'ทำอะไรได้ / ใครทำ'],
          rows: [
            ['ฉบับร่าง', 'จัดทำแล้วแต่ยังไม่ส่งอนุมัติ', 'แก้ไข / ส่งอนุมัติ / ลบ — ผู้จัดทำ'],
            [
              'รออนุมัติ',
              'ส่งแล้ว กำลังรอพิจารณา',
              'พิจารณา (อนุมัติ/ตีกลับ/ไม่อนุมัติ) — ผู้ตรวจสอบ/ผู้อนุมัติ',
            ],
            ['ส่งกลับแก้ไข', 'ผู้พิจารณาตีกลับให้แก้', 'แก้ไขแล้วส่งใหม่ — ผู้จัดทำ'],
            ['อนุมัติแล้ว', 'อนุมัติครบ รอผ่านรายการ', 'ผ่านรายการ — ผู้มีสิทธิ์โพสต์'],
            ['โพสต์แล้ว', 'ลงบัญชีแยกประเภทเรียบร้อย', 'พิมพ์ / ดู GL / สร้างจากใบนี้'],
            ['ไม่อนุมัติ', 'ถูกปฏิเสธ พร้อมเหตุผล', 'ดูได้อย่างเดียว'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '7. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมปุ่ม “บันทึกร่าง” กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ยอดเดบิตยังไม่เท่ากับเครดิต (แถบยังเป็นสีแดง)' },
            { text: 'ยังมีบรรทัดที่ไม่ได้เลือกบัญชี หรือยังไม่ได้กรอกรายละเอียด' },
            { text: 'วันที่ที่เลือกไม่อยู่ในงวดที่เปิด' },
          ],
        },
        { kind: 'para', text: 'ทำไมไม่เห็นปุ่ม “สร้างใบสำคัญ” หรือ “พิจารณา”?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เป็นเรื่องสิทธิ์การใช้งาน — ปุ่มแสดงเฉพาะผู้ที่มีสิทธิ์/บทบาทตรงกับการกระทำนั้น',
            },
            { text: 'ปุ่ม พิจารณา ไม่ขึ้นกับผู้ที่ส่งอนุมัติใบนั้นเอง (แบ่งแยกหน้าที่)' },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '8. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'JV — ',
              text: 'ใบสำคัญทั่วไป (Journal Voucher) เอกสารบันทึกรายการบัญชีหนึ่งชุด',
            },
            {
              lead: 'เดบิต / เครดิต — ',
              text: 'ด้านซ้าย/ด้านขวาของบัญชีคู่ (รวมสองด้านต้องเท่ากัน)',
            },
            { lead: 'งวด — ', text: 'รอบระยะเวลาบัญชีรายเดือน รูปแบบ ปปปปดด เช่น 202606' },
            {
              lead: 'ผ่านรายการ/โพสต์ — ',
              text: 'การลงบัญชีเข้าสมุดแยกประเภทจริง (หลังจากนี้แก้ไม่ได้ ต้องกลับรายการแทน)',
            },
            {
              lead: 'Maker–Checker — ',
              text: 'การควบคุมแบบสี่ตา ผู้จัดทำ–ผู้ตรวจสอบ–ผู้อนุมัติต้องเป็นคนละคน',
            },
          ],
        },
      ],
    },
  ],
};
